import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ShieldAlert, BadgeCheck, CheckCircle2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { getUser, formatDate } from "@/lib/user";
import { MockAccountsSection } from "@/components/MockAccountsSection";
import { BastionPanel } from "@/components/BastionPanel";
import { SecuritePanel } from "@/components/SecuritePanel";
import { PaiementsDirectsSection } from "@/components/PaiementsDirectsSection";
import { DeblocageClientSection } from "@/components/DeblocageClientSection";
import { NettoyageDonneesSection } from "@/components/NettoyageDonneesSection";
import { RevenusPanel } from "@/components/RevenusPanel";
import { ModerationAirBengue } from "@/components/ModerationAirBengue";
import { NumerosAirBengue } from "@/components/NumerosAirBengue";
import { InvitationsAdmin } from "@/components/InvitationsAdmin";
import { CadeauxAdmin } from "@/components/CadeauxAdmin";
import { GestionAnnoncesAdmin } from "@/components/GestionAnnoncesAdmin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const user = getUser();
  const [reports, setReports] = useState([]);
  const [interdit, setInterdit] = useState(false);
  const [ageStats, setAgeStats] = useState(null);

  const charger = useCallback(() => {
    if (!user) return;
    axios
      .get(`${API}/admin/signalements`, { params: { admin_id: user.id } })
      .then((r) => setReports(r.data))
      .catch((err) => {
        if (err.response?.status === 403) setInterdit(true);
      });
    axios.get(`${API}/age-verification/stats`).then((r) => setAgeStats(r.data)).catch(() => {});
  }, [user?.id]);

  useEffect(() => { charger(); }, [charger]);

  if (!user) return <Navigate to="/connexion" replace />;

  const traiter = async (id) => {
    try {
      await axios.post(`${API}/admin/signalements/${id}/traiter`, { admin_id: user.id });
      toast.success("Signalement marqué comme traité.");
      charger();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const verifierProfil = async (profileId, verified) => {
    try {
      await axios.post(`${API}/admin/profiles/${profileId}/verifier`, { admin_id: user.id, verified });
      toast.success(verified ? "Profil vérifié (badge activé)." : "Vérification retirée.");
      charger();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="bg-[#FAF8F5] min-h-screen">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 pt-32 pb-24" data-testid="admin-page">
        <span className="text-[#E05638] text-sm font-semibold tracking-[0.25em] uppercase">Administration</span>
        <h1 className="font-heading text-4xl font-extrabold tracking-tight text-stone-900 mt-2 flex items-center gap-3">
          <ShieldAlert size={30} className="text-[#E05638]" /> Signalements en attente
        </h1>

        {ageStats && !interdit && (
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="admin-age-stats">
            {[
              { label: "Confirmations +18", valeur: ageStats.total },
              { label: "Aujourd'hui", valeur: ageStats.aujourdhui },
              { label: "7 derniers jours", valeur: ageStats.sept_jours },
              { label: "Visiteurs uniques", valeur: ageStats.visiteurs_uniques },
            ].map((s) => (
              <div key={s.label} className="bg-white border border-[#E5DFD5] rounded-2xl p-4">
                <div className="font-heading text-3xl font-extrabold text-stone-900">{s.valeur}</div>
                <div className="text-xs font-medium text-stone-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {!interdit && <DeblocageClientSection adminId={user.id} />}
        {!interdit && <NettoyageDonneesSection adminId={user.id} />}

        {interdit ? (
          <p className="mt-10 text-sm text-red-600 font-semibold" data-testid="admin-interdit">
            Accès réservé aux administrateurs.
          </p>
        ) : reports.length === 0 ? (
          <p className="mt-10 text-sm text-stone-500" data-testid="admin-vide">Aucun signalement en attente. 🎉</p>
        ) : (
          <div className="mt-8 space-y-4">
            {reports.map((r) => (
              <div key={r.id} data-testid={`admin-report-${r.id}`} className="bg-white border border-[#E5DFD5] rounded-2xl p-5">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                  <span className="font-bold text-stone-900">{r.cible.firstname}</span>
                  {r.cible.verified && <BadgeCheck size={15} className="text-[#1D9BF0]" />}
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">{r.motif}</span>
                  <span className="text-stone-400 text-xs">signalé par {r.reporter.firstname} · {formatDate(r.created_at)}</span>
                </div>
                {r.commentaire && <p className="text-sm text-stone-600 mt-2 italic">« {r.commentaire} »</p>}
                <div className="flex flex-wrap gap-2 mt-4">
                  <button
                    onClick={() => traiter(r.id)}
                    data-testid={`admin-traiter-${r.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold bg-stone-900 hover:bg-stone-700 text-white px-4 py-2 rounded-full transition-colors"
                  >
                    <CheckCircle2 size={13} /> Marquer traité
                  </button>
                  <button
                    onClick={() => verifierProfil(r.cible_id, !r.cible.verified)}
                    data-testid={`admin-verifier-${r.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold border border-[#1D9BF0] text-[#1D9BF0] hover:bg-[#1D9BF0] hover:text-white px-4 py-2 rounded-full transition-colors"
                  >
                    <BadgeCheck size={13} /> {r.cible.verified ? "Retirer la vérification" : "Vérifier ce profil"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!interdit && <RevenusPanel adminId={user.id} />}
        {!interdit && <ModerationAirBengue adminId={user.id} />}
        {!interdit && <NumerosAirBengue adminId={user.id} />}
        {!interdit && <InvitationsAdmin adminId={user.id} />}
        {!interdit && <CadeauxAdmin adminId={user.id} />}
        {!interdit && <GestionAnnoncesAdmin adminId={user.id} />}
        {!interdit && <PaiementsDirectsSection adminId={user.id} />}
        {!interdit && <BastionPanel user={user} />}
        {!interdit && <SecuritePanel adminId={user.id} />}
        {!interdit && <MockAccountsSection adminId={user.id} />}
      </main>
      <Footer />
    </div>
  );
}
